import { Injectable, QueryList } from '@angular/core';
import { BehaviorSubject, of, Observable, Subject, tap, debounceTime, switchMap, delay, map } from 'rxjs';
import { SortColumn, SortDirection, SortEvent, SortableHeader } from './sortable.directive';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ITableService, SearchResult } from './table.service.models';

interface State {
    page: number;
    pageSize: number;
    searchTerm: string;
    sortColumn: string;
    sortDirection: string;
}

@Injectable()
export class TableService<TPagedResponseType extends { data?: Tdata[] | undefined, totalSize?: number | undefined }, Tdata> implements ITableService {
    public loadItems$: (page: number, pageSize: number, sort: SortEvent) => Observable<TPagedResponseType>;

    private _loading$ = new BehaviorSubject<boolean>(true);
    private _search$ = new Subject<void>();
    private _tables$ = new BehaviorSubject<Tdata[]>([]);
    private _total$ = new BehaviorSubject<number>(0);
    private _headers: QueryList<SortableHeader>;
    private _sortEvent: SortEvent;

    private _state: State = {
        page: 1,
        pageSize: parseInt(localStorage.getItem("defaultPageSize") ?? '10'),
        searchTerm: '',
        sortColumn: '',
        sortDirection: ''
    };

    constructor(private loadingService: LoadingBarService) {

        this._search$
            .pipe(
                tap(() => this._loading$.next(true)),
                debounceTime(200),
                switchMap(() => this._search()),
                tap(() => this._loading$.next(false))
            )
            .subscribe(result => {
                this._tables$.next(result.basicTable);
                this._total$.next(result.total);
            });

        this.loading$.subscribe(loading => {
            if (loading)
                this.loadingService.useRef('table').start();
            else
                this.loadingService.useRef('table').stop();
        });

        this._search$.next();
    }

    refreshList() {
        this._search$.next();
    }

    onSort(event: unknown) {
        this._sortEvent = event as SortEvent;

        if (this._sortEvent) {
            // resetting other headers
            this._headers.forEach((header) => {
                if (header.sortable !== this._sortEvent.column) {
                    header.direction = '';
                }
            });
            this.sortColumn = this._sortEvent.column;
            this.sortDirection = this._sortEvent.direction;
            this._search$.next();
        }
    }

    get basicTable$() { return this._tables$.asObservable(); }
    get data() { return this._tables$.value; }
    get total$() { return this._total$.asObservable(); }
    get loading$() { return this._loading$.asObservable(); }
    get page() { return this._state.page; }
    get pageSize() { return this._state.pageSize; }
    get searchTerm() { return this._state.searchTerm; }

    set page(page: number) {
        this._set({ page });
    }
    set pageSize(pageSize: number) {
        this._set({ pageSize });
    }
    set searchTerm(searchTerm: string) { this._set({ searchTerm }); }
    set sortColumn(sortColumn: string) { this._set({ sortColumn }); }
    set sortDirection(sortDirection: string) { this._set({ sortDirection }); }
    set headers(headers: QueryList<SortableHeader>) { this._headers = headers }

    private _set(patch: Partial<State>) {
        Object.assign(this._state, patch);
        this._search$.next();
    }

    private _search(): Observable<SearchResult<Tdata>> {
        return this.loadItems$(this.page, this.pageSize, this._sortEvent)
            .pipe(map((results: any) => <SearchResult<Tdata>>{ basicTable: results.data, total: results.totalSize }));
    }


}
